'use client';

import type { AgendaBlock, GatheringDoor } from '@/lib/types';
import { emphasisRuns } from '@/lib/emphasis';
import { fmtClock } from '@/lib/format';

/**
 * WHAT HAPPENS, AND WHEN
 * ======================
 *
 * The thing every parent asks and most event pages answer with a paragraph.
 * Blocks come from the gathering's sessions, in order, with the time on the
 * left the way a run of show reads.
 *
 * A BLOCK CAN BE FOR SOME OF THE ROOM. A volunteer call time is on the same
 * schedule as the night itself, and so is the moment leaders move students
 * inside, but neither is for the student reading this. Those carry the
 * registrations they belong to, and a household only sees one once somebody
 * in it is actually registered that way: a call time for a job nobody here
 * signed up for is a schedule for somebody else's night. Once it is theirs
 * it keeps its chip, so a parent reading one agenda can still tell which
 * lines are the volunteer's and which are everybody's.
 */

export interface AgendaProps {
  blocks: AgendaBlock[];
  doors: GatheringDoor[];
  /** Registration slugs somebody in this household is registered for. */
  mine?: Set<string>;
}

export function Agenda({ blocks, doors, mine }: AgendaProps) {
  const labelOf = new Map(doors.map((d) => [d.slug ?? String(d.id), d.label]));
  const shown = blocks.filter(
    (b) => b.audience.length === 0 || b.audience.some((slug) => mine?.has(slug))
  );
  if (!shown.length) return null;

  return (
    <section>
      <h2 className="kicker mb-3">Agenda</h2>
      <ol className="card divide-mist divide-y">
        {shown.map((block) => (
          <li key={block.id} className="flex gap-4 p-4">
            {/* When it starts. The end time was on every row and answered a
                question the next row already answers. */}
            <div className="w-16 shrink-0 text-right">
              <div className="text-navy text-sm font-extrabold">{fmtClock(block.start)}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-navy font-bold">{block.title}</h3>
                {block.audience.map((slug) => (
                  <span
                    key={slug}
                    className="bg-mist text-slate-deep rounded-full px-2 py-0.5 text-[0.65rem] font-bold tracking-wide uppercase"
                  >
                    {labelOf.get(slug) ?? slug}
                  </span>
                ))}
              </div>
              {block.description && (
                <p className="text-slate-deep mt-0.5 text-sm">
                  {emphasisRuns(block.description).map((run, i) =>
                    run.emphasis ? (
                      <em key={i} className="text-navy font-semibold">
                        {run.text}
                      </em>
                    ) : (
                      run.text
                    )
                  )}
                </p>
              )}
              {block.room && <p className="text-slate mt-0.5 text-xs">{block.room}</p>}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
